import type { DomainHealth } from '@smart-link/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';

export interface DomainsResponse {
  default: string;
  domains: {
    name: string;
    status: string;
    createdOn?: string | null;
    disabled?: boolean;
    // 明确绑定到该域名 (link.domain === name) 的链接数，含 paused
    linkCount?: number;
    // 备注 + 添加人快照（domain_meta 表，历史域名为空）
    note?: string;
    createdByName?: string;
    createdByAvatar?: string;
    health?: DomainHealth & { checkedAt: string };
  }[];
  // domain='' (跟随默认域名) 的链接数，前端加到「默认」域名行上
  followDefaultLinkCount?: number;
  // 手动指定的默认域名（rules 表 domains:default），空 = 第一个未停用
  preferredDefault?: string;
  error?: string;
}

const PROJECT_DEFAULT = 'smart-link-page.pages.dev';

export function useDomains() {
  return useQuery({
    queryKey: ['domains'],
    queryFn: () => api<DomainsResponse>('/api/domains'),
    staleTime: 5 * 60_000,
  });
}

export type { DomainHealth, DomainStatus } from '@smart-link/shared';

// 服务身份健康检测：把域名列表发给 worker，拿回每个域名的可用性判定。
export function useDomainHealth(names: string[]) {
  return useQuery({
    queryKey: ['domain-health', ...names],
    enabled: names.length > 0,
    staleTime: 5 * 60_000,
    queryFn: () =>
      api<{ results: DomainHealth[] }>('/api/domains/health', {
        method: 'POST',
        body: JSON.stringify({ domains: names }),
      }),
  });
}

export interface DomainOption {
  value: string;
  label: string;
  disabled?: boolean;
}

type ListedDomain = DomainsResponse['domains'][number];

function isDomainSelectable(domain: ListedDomain): boolean {
  const healthStatus = domain.health?.status;
  return (
    !domain.disabled &&
    domain.status === 'active' &&
    healthStatus !== 'dead' &&
    healthStatus !== 'suspended'
  );
}

function domainOptionLabel(domain: ListedDomain): string {
  if (domain.disabled) return `${domain.name} (已停用)`;
  if (domain.status !== 'active') return `${domain.name} (${domain.status})`;
  if (domain.health?.status === 'dead') return `${domain.name} (已失效)`;
  if (domain.health?.status === 'suspended') return `${domain.name} (已暂停)`;
  return domain.name;
}

/** Resolve the safe default from active domains whose latest health is not bad. */
export function activeDefault(data: DomainsResponse): string {
  const preferred = data.preferredDefault;
  if (preferred && data.domains.some((d) => d.name === preferred && isDomainSelectable(d))) {
    return preferred;
  }
  return (
    data.domains.find((domain) => isDomainSelectable(domain) && domain.health?.status === 'healthy')
      ?.name ??
    data.domains.find(isDomainSelectable)?.name ??
    data.default
  );
}

// Keep unavailable domains visible for existing links, but gray them out for new selection.
// `unknown` remains selectable so a probe outage cannot disable every domain at once.
export function domainOptions(data?: DomainsResponse): DomainOption[] {
  if (!data) return [{ value: '', label: '默认' }];
  return [
    { value: '', label: `默认 (${activeDefault(data)})` },
    ...data.domains.map((domain) => ({
      value: domain.name,
      label: domainOptionLabel(domain),
      disabled: !isDomainSelectable(domain),
    })),
  ];
}

// Public short-link URL: link 自选域名优先；未选则用第一个未停用域名；
// 仅当无任何可用自定义域名时才回退到项目默认 (pages.dev)。
export function publicLinkUrl(linkId: string, domain: string, data?: DomainsResponse): string {
  const fallback = data ? activeDefault(data) : PROJECT_DEFAULT;
  const host = domain || fallback;
  return `https://${host}/${linkId}`;
}
